/**
 * Context bindings — show view (the context ↔ entity edge), on the shared
 * Inspector grammar (design-system §5). Both ends of the edge — the `context_id`
 * and the bound `entity_id` — resolve to RefLinks (coverage §2:
 * "Context · bound entity (both RefLinks)").
 */
import { Boxes, Layers, Pencil } from "@/components/ui/icon";
import type { ContextBinding } from "@aos/shared";

import { useGo, useNavigation } from "@refinedev/core";
import { Button } from "@/components/ui/button";
import {
  Field,
  RefLink,
  ShowPage,
  type RelationGroup,
} from "@/components/inspector";
import { humanize } from "@/lib/format";
import { formatDateTime } from "../_shared/runs-ui";

/** Map a binding `entity_type` to the Refine resource the `entity_id` links to. */
const ENTITY_TYPE_RESOURCE: Record<string, string> = {
  artifact: "artifacts",
  package: "packages",
  skill: "skills",
  contour: "processes",
  process: "processes",
  signal: "signals",
  rule: "rules",
  document: "documents",
  bet: "bets",
  frame: "frames",
  principle: "principles",
  metric: "metrics",
  decision: "decisions",
  objective: "objectives",
  key_result: "key-results",
  phase: "phases",
};

export function ContextBindingShow() {
  const go = useGo();
  const { editUrl } = useNavigation();

  return (
    <ShowPage<ContextBinding>
      resource="context-bindings"
      title={() => "Context Binding"}
      subtitle="An edge connecting a context to an artifact, package, rule, or actor."
      status={(b) => b.access_mode}
      actions={(b) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => go({ to: editUrl("context-bindings", b.id) })}
        >
          <Pencil className="mr-2 h-4 w-4" /> Edit
        </Button>
      )}
      overview={(b) => (
        <>
          <Field label="Binding type">{humanize(b.binding_type)}</Field>
          <Field label="Entity type">{humanize(b.entity_type)}</Field>
          <Field label="Access mode">{humanize(b.access_mode)}</Field>
          <Field label="Priority">{b.priority}</Field>
          <Field label="Active from">{formatDateTime(b.active_from)}</Field>
          <Field label="Active to">{formatDateTime(b.active_to)}</Field>
        </>
      )}
      technical={(b) => (
        <>
          <Field label="Created">{formatDateTime(b.created_at)}</Field>
          <Field label="Updated">{formatDateTime(b.updated_at)}</Field>
        </>
      )}
      relations={(b): RelationGroup[] => {
        const entityResource = ENTITY_TYPE_RESOURCE[b.entity_type];
        return [
          {
            title: "References",
            items: [
              {
                label: "Context",
                icon: Layers,
                resource: "contexts",
                id: b.context_id,
              },
              {
                label: `Bound ${humanize(b.entity_type)}`,
                icon: Boxes,
                resource: entityResource,
                id: entityResource ? b.entity_id : undefined,
                node: entityResource ? undefined : (
                  <span className="font-mono text-xs">{b.entity_id}</span>
                ),
              },
            ],
          },
        ];
      }}
    />
  );
}

export default ContextBindingShow;
